import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { Layout, Menu, Tooltip } from "antd";
import { observer } from "mobx-react";
import { SidebarDrawer } from "./SidebarDrawer";
const { Sider } = Layout;
import { SidebarSubMenu } from "./SidebarSubMenu";
import { I18N_NS } from "Config/i18n";
import { debounce } from "lodash";
import { SubmenuItem } from "./subMenus";
import { MenuItems, sidebarStore } from "./SidebarStore";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { ItemType, SubMenuType } from "antd/lib/menu/interface";
import { pdfViewerStore } from "Stores/PdfViewerStore";

export interface SidebarItemProps {
  label: string;
  iconName: string;
  /**
   * Link to the specific Module
   */
  link?: string;
  subMenu?: SubmenuItem[];
}

export interface SidebarMenuProps extends WithTranslation, WithRouterProps {
  menuItems: {
    mainMenuItems: MenuItems[];
    secondaryMenuItems: MenuItems[];
  };
  projectID: number;
  pathname: string;
  onLink: (x: string) => void;
}

export const SidebarMenu = withTranslation(I18N_NS.PROJECT)(
  withRouter(
    observer(
      class extends React.Component<SidebarMenuProps> {
        componentDidUpdate(prevProps: SidebarMenuProps) {
          if (this.props.menuItems !== prevProps.menuItems) {
            sidebarStore.setActiveRoutes(null, this.props.pathname);
          }
          /**
           * Update sidebar active menu item & submenus after browser's back button pressed
           */
          if (prevProps.pathname !== this.props.pathname) {
            sidebarStore.setActiveRoutes(this.parseRoute(this.props.pathname), this.props.pathname);
          }
        }

        componentDidMount() {
          // Temporary fix for initial opening of sidebar submenu.
          window.setTimeout(() => {
            this.forceUpdate();
          }, 500);
          const initialLink: string = this.props.pathname?.substring(1).split("/").slice(0, 3).join("/");
          sidebarStore.fetch();
          sidebarStore.setActiveRoutes(`/${initialLink}`, this.props.pathname);
        }

        parseRoute = (route: string) => {
          return `/${route
            .split("/")
            .filter((v) => v)
            .splice(0, 3)
            .join("/")}`;
        };

        handleLink = (link: string, path: string = null) => {
          this.props.onLink(link || path);
          sidebarStore.setActiveRoutes(link, path);
        };

        isActiveLink = (link: string) => {
          return sidebarStore.activePath.indexOf(link) !== -1;
        };

        createMenuItem = ({ link, iconName, label, onClick }: Partial<MenuItems>): ItemType => {
          return {
            key: link,
            className: `${this.isActiveLink(link) ? "ant-menu-item-selected" : ""}`,
            label: (
              <div onClick={() => (onClick ? onClick() : this.handleLink(link))}>
                <div className="sidebar-icon">
                  <FontAwesomeIcon icon={["far", iconName as IconName]} size="lg" fixedWidth={true} />
                </div>
                <span>{this.props.t(label)}</span>
              </div>
            ),
          };
          // return (
          //   <Menu.Item key={link} className={`${this.isActiveLink(link) ? "ant-menu-item-selected" : ""}`}>
          //     <div onClick={() => this.handleLink(link)}>
          //       <div className="sidebar-icon">
          //         <FontAwesomeIcon icon={["far", iconName as IconName]} size="lg" fixedWidth={true} />
          //       </div>
          //       <span>{this.props.t(label)}</span>
          //     </div>
          //   </Menu.Item>
          // );
        };
        createSubMenu = ({
          link,
          iconName,
          label,
          subMenu,
        }: {
          link: string;
          iconName: string;
          label: string;
          subMenu: SubmenuItem[];
        }): SubMenuType => {
          const renderIcon = (item: SubmenuItem) => {
            if (item.icon) {
              return typeof item.icon === "string" ? (
                <div className="sidebar-icon">
                  <FontAwesomeIcon icon={["fas", item.icon as IconName]} fixedWidth={true} size="lg" />
                </div>
              ) : (
                item.icon
              );
            }
            return null;
          };
          return {
            key: link,
            popupClassName: "hidden",
            className: `${this.isActiveLink(link) ? "ant-menu-item-selected" : ""}`,
            label: (
              <div onClick={() => this.handleLink(link, null)} className="sidebar-link text-ellipsis">
                <div className="sidebar-icon">
                  <FontAwesomeIcon
                    icon={["far", iconName as IconName]}
                    size="lg"
                    fixedWidth={true}
                    style={{ marginRight: 12 }}
                  />
                </div>
                <span>{label}</span>
              </div>
            ),
            children: subMenu
              ?.map((submenuItem) => {
                return {
                  label: (
                    <Tooltip title={submenuItem.message} placement="right">
                      <div
                        onClick={() => this.handleLink(null, `${link}/${submenuItem.link}`)}
                        className="sidebar-link"
                      >
                        {renderIcon(submenuItem)}
                        {submenuItem.label}
                      </div>
                    </Tooltip>
                  ),
                  className: `${
                    (
                      submenuItem.isActive
                        ? submenuItem.isActive(`${link}/${submenuItem.link}`)
                        : this.isActiveLink(`${link}/${submenuItem.link}`)
                    )
                      ? "ant-menu-item-selected"
                      : ""
                  }`,
                  key: `${link}/${submenuItem.link}`,
                };
              })
              .filter(Boolean),
          };
        };
        renderSubMenu = ({
          link,
          iconName,
          label,
          subMenu,
        }: {
          link: string;
          iconName: string;
          label: string;
          subMenu: SubmenuItem[];
        }) => {
          return (
            <SidebarSubMenu
              key={link}
              link={link}
              iconName={iconName}
              titleLabel={this.props.t(label)}
              subMenu={subMenu}
              isActive={(link) => this.isActiveLink(link)}
              onLink={this.handleLink}
            />
          );
        };
        mapMenu = (arr: MenuItems[]) => {
          return arr
            .map((val) => {
              const { label, iconName, link, module, onClick } = val;
              if (!val.hidden) {
                // If submenu is defined for current menu item
                if (sidebarStore.subMenus[module]) {
                  return this.createSubMenu({
                    label: this.props.t(label),
                    iconName,
                    link,
                    subMenu: sidebarStore.subMenus[module].config,
                  });
                } else {
                  return this.createMenuItem({ label: this.props.t(label), iconName, link, onClick });
                }
              }
              return null;
            })
            .filter(Boolean);
        };

        render() {
          const { menuItems } = this.props;
          const firstMenuItemsBlock = [
            this.createMenuItem({ label: "Dashboard", iconName: "home", link: "/dashboard" }),
            ...this.mapMenu(menuItems.mainMenuItems),
          ];
          return (
            <div>
              <Menu
                theme="dark"
                mode="inline"
                className="sidebar-menu"
                selectedKeys={[sidebarStore.activeLink, sidebarStore.activePath]}
                openKeys={[sidebarStore.activeLink, sidebarStore.activePath]}
                items={firstMenuItemsBlock}
              ></Menu>
              <div style={{ marginTop: 15 }} />
              <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[sidebarStore.activeLink, sidebarStore.activePath]}
                openKeys={[sidebarStore.activeLink, sidebarStore.activePath]}
                className="sidebar-menu"
                items={this.mapMenu(menuItems.secondaryMenuItems)}
              ></Menu>
            </div>
          );
        }
      }
    )
  )
);

interface SideBarProps extends WithRouterProps {
  currentProject: ProjectItemUI;
}

export const Sidebar = withRouter(
  observer(
    class Sidebar extends React.Component<SideBarProps, {}> {
      componentDidMount() {
        window.addEventListener("resize", debounce(this.handleTriggerCollapse, 200));
        setTimeout(() => {
          this.handleTriggerCollapse();
        }, 300);

        (async () => sidebarStore.fetch())();
      }

      handleTriggerCollapse = () => {
        const width = window.innerWidth;
        // pdfViewerStore.isVisibleSplitView check is used to hide sidebar when splitted view is enabled
        if (width < 1025 || (pdfViewerStore.isVisibleSplitView && width < 1820)) {
          sidebarStore.setSidebarCollapse(true);
        } else {
          sidebarStore.setSidebarCollapse(false);
        }
      };

      componentWillUnmount() {
        sidebarStore.clear();
      }

      render() {
        const { currentProject } = this.props;
        if (currentProject) {
          const { id } = currentProject;
          const menuWithLink = currentProject.modules.map((item) => ({
            ...item,
            link: `/project/${id}/${item.link}`,
          }));
          menuWithLink.unshift({
            iconName: "info-circle",
            label: currentProject.name,
            link: `/project/${id}/overview`,
          });

          const mainMenuItems = sidebarStore.menu?.filter((m) => !m.secondary);
          const secondaryMenuItems = sidebarStore.menu?.filter((m) => m.secondary);
          return (
            <>
              <Sider
                collapsed={sidebarStore.isCollapsed}
                trigger={null}
                width={240}
                // breakpoint="lg" // 992px
                collapsedWidth={0}
                onCollapse={() => sidebarStore.toggleSidebar()}
                className="project-sidebar"
              >
                <SidebarMenu
                  onLink={(link) => this.props.navigate(link)}
                  menuItems={{ mainMenuItems, secondaryMenuItems }}
                  projectID={id}
                  pathname={this.props.location.pathname}
                />
              </Sider>
              <SidebarDrawer showDrawer={!sidebarStore.isCollapsed} />
            </>
          );
        } else {
          return null;
        }
      }
    }
  )
);
